<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div ref="exportTarget">
        <div ref="exportHeader" class="export-header" data-testid="export-header">
          <div>{{ placeName }} / {{ selectedMonth }}</div>
          <div>{{ floorPlanName }} / {{ filterLabel }}</div>
        </div>
        <v-list dense data-testid="item-ranking-list">
          <v-list-item
            v-for="(item, index) in topItems"
            :key="item.id"
            class="ranking-item"
          >
            <v-list-item-icon class="ranking-number-icon">
              <span class="ranking-badge" :style="{ color: getRankColor(index) }">
                {{ $t('rank', { n: index + 1 }) }}
              </span>
            </v-list-item-icon>
            <v-list-item-content>
              <div
                v-if="typeLabel(item)"
                class="ranking-type"
                data-testid="item-type-label"
              >{{ typeLabel(item) }}</div>
              <v-list-item-title
                :title="item.label"
                style="font-weight: bold; color: rgba(0,0,0,0.87)"
              >{{ item.label }}</v-list-item-title>
              <v-list-item-subtitle
                style="color: rgba(0,0,0,0.6)"
              >{{ $t(`${type}.count`, { count: item.count }) }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="topItems.length === 0">
            <v-list-item-content>
              <v-list-item-title class="text--secondary">{{ $t('no_data') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
    </v-card-text>
  </v-card>
</template>

<i18n lang="yaml">
ja:
  rank: "【{n}】"
  no_data: データがありません
  sensor:
    count: "検出数: {count}"
    export:
      ranking_type: センサーランキング
      csv:
        location: 管理施設
        floor_plan: 図面
        period: 期間
        filter: 検出種別
        rank: 順位
        item_type: 種類
        item_name: センサー名
        item_count: 検出数
  trap:
    count: "捕獲数: {count}"
    export:
      ranking_type: トラップランキング
      csv:
        location: 設置場所
        floor_plan: 図面
        period: 期間
        filter: 虫種フィルタ
        rank: 順位
        item_type: 種類
        item_name: トラップ番号
        item_count: 捕獲数

en:
  rank: "[{n}]"
  no_data: No data available
  sensor:
    count: "Detections: {count}"
    export:
      ranking_type: Sensor Ranking
      csv:
        location: Facility
        floor_plan: Floor Plan
        period: Period
        filter: Detection Filter
        rank: Rank
        item_type: Type
        item_name: Sensor Name
        item_count: Detection Count
  trap:
    count: "Capture Count: {count}"
    export:
      ranking_type: Trap Ranking
      csv:
        location: Place
        floor_plan: Floor Plan
        period: Period
        filter: Insect Filter
        rank: Rank
        item_type: Type
        item_name: Trap No.
        item_count: Capture Count
</i18n>

<script>
import SensorTypes from '@/mixins/sensorTypes';

import {
  generateRankingCsvString,
  buildRankingFileName,
  exportRankingImage as defaultExportRankingImage,
  exportRankingCsv as defaultExportRankingCsv,
} from './rankingExport';

const RANKING_LIMIT = 5;
const RANK_COLORS = ['#d32f2f', '#ef6c00', '#9e9d24', '#757575', '#757575'];

export default {
  name: 'FloorPlanItemRanking',
  mixins: [SensorTypes],
  inject: {
    exportRankingImage: {
      default() {
        return defaultExportRankingImage;
      },
    },
    exportRankingCsv: {
      default() {
        return defaultExportRankingCsv;
      },
    },
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: v => ['sensor', 'trap'].includes(v),
    },
    filterLabel: {
      type: String,
      default: '',
    },
    placeName: {
      type: String,
      default: '',
    },
    floorPlanName: {
      type: String,
      default: '',
    },
    selectedMonth: {
      type: String,
      default: '',
    },
  },
  computed: {
    rankedItems() {
      return this.items
        .filter(item => item.isPlaced)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.label.localeCompare(b.label);
        });
    },
    topItems() {
      return this.rankedItems.slice(0, RANKING_LIMIT);
    },
    csvMetaRows() {
      return [
        [this.$mpt(this.$t(`${this.type}.export.csv.location`)), this.placeName],
        [this.$mpt(this.$t(`${this.type}.export.csv.floor_plan`)), this.floorPlanName],
        [this.$mpt(this.$t(`${this.type}.export.csv.period`)), this.selectedMonth],
        [this.$mpt(this.$t(`${this.type}.export.csv.filter`)), this.filterLabel],
      ];
    },
  },
  methods: {
    getRankColor(index) {
      return RANK_COLORS[index] || '#757575';
    },
    typeLabel(item) {
      return item.type ? this.SENSOR_TYPE_NAMES[item.type] : null;
    },
    async exportImage() {
      const header = this.$refs.exportHeader;
      header.style.display = 'block';
      await this.$nextTick();
      try {
        const fileName = buildRankingFileName(
          this.placeName,
          this.floorPlanName,
          this.selectedMonth,
          this.$mpt(this.$t(`${this.type}.export.ranking_type`)),
          'png',
        );
        await this.exportRankingImage(this.$refs.exportTarget, fileName);
      } finally {
        header.style.display = '';
      }
    },
    exportCsv() {
      const headers = [
        this.$mpt(this.$t(`${this.type}.export.csv.rank`)),
        this.$mpt(this.$t(`${this.type}.export.csv.item_type`)),
        this.$mpt(this.$t(`${this.type}.export.csv.item_name`)),
        this.$mpt(this.$t(`${this.type}.export.csv.item_count`)),
      ];
      const dataRows = this.rankedItems.map((item, index) => [
        index + 1, this.typeLabel(item) || '', item.label, item.count,
      ]);
      const csvString = generateRankingCsvString(this.csvMetaRows, headers, dataRows);
      const fileName = buildRankingFileName(
        this.placeName,
        this.floorPlanName,
        this.selectedMonth,
        this.$mpt(this.$t(`${this.type}.export.ranking_type`)),
        'csv',
      );
      this.exportRankingCsv(csvString, fileName);
    },
  },
};
</script>

<style scoped>
.ranking-item {
  border-bottom: 1px solid #e0e0e0;
}

.ranking-item:last-child {
  border-bottom: none;
}

.ranking-badge {
  font-weight: bold;
  font-size: 1.1em;
  white-space: nowrap;
}

/* 順位バッジは Vuetify 既定で上寄せになるが、
   種類ラベルを足して行が 3 段になったため中央に揃える */
.ranking-item .ranking-number-icon {
  min-width: 40px;
  align-self: center;
  margin-top: 0;
  margin-bottom: 0;
}

.ranking-type {
  font-size: 11px;
  color: #9e9e9e;
  line-height: 1.3;
  letter-spacing: 0.03em;
}

.export-header {
  display: none;
  padding: 8px 16px;
}

.export-header div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
